'use client';

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { IconPlay } from '@/components/icons/IconPlay';
import { UiMediaLightbox } from '@/components/ui/UiMediaLightbox';
import type { MediaDto } from '@/lib/sanity/dto';

type MediaLightboxFrameProps = {
    /** The film — the same one the loop inside is showing, played here with its sound. */
    video: MediaDto;
    /** Its first frame, for the moment before the film in the dialog has decoded. */
    poster?: MediaDto;
    /** Accessible name of the play button, and of the dialog it opens. */
    label: string;
    /** The frame's own class — the block decides how big the box is and what shape. */
    className?: string;
    /** The loop, rendered by the server component that owns this frame. */
    children: ReactNode;
};

/**
 * A silent loop with a play button on it, and the film itself one click away.
 *
 * The loop is the block's own markup, passed through as children: it stays
 * server-rendered, and only the button and the dialog hydrate. The frame
 * element is this component's, because the button is positioned against it and
 * because the pause below needs something it owns to look inside.
 *
 * It keeps its own lightbox rather than going through MediaLightboxProvider:
 * that one takes a MediaCardData, whose title it then draws as a caption, and
 * the design gives this dialog no caption at all — only the film and the close
 * button. HeroShowreelButton opens the same box on the same terms.
 */
export function MediaLightboxFrame({ video, poster, label, className, children }: MediaLightboxFrameProps) {
    const frameRef = useRef<HTMLDivElement>(null);
    const [isOpen, setOpen] = useState(false);

    // Nothing is audible either way — the loop is muted — but a film decoding
    // behind an opaque backdrop is work for no viewer, so it stands still while
    // the dialog is up and picks up again underneath once it closes.
    useEffect(() => {
        const loop = frameRef.current?.querySelector('video');
        if (!loop) return;

        if (isOpen) loop.pause();
        else void loop.play().catch(() => {});
    }, [isOpen]);

    return (
        <div ref={frameRef} className={className}>
            {children}

            <button type="button" className="lightbox-frame__play" onClick={() => setOpen(true)} aria-label={label}>
                <span className="lightbox-frame__chip" aria-hidden="true">
                    <IconPlay />
                </span>
            </button>

            <UiMediaLightbox
                video={video}
                poster={poster}
                label={label}
                open={isOpen}
                onClose={() => setOpen(false)}
                closeLabel="Video schließen"
            />
        </div>
    );
}
